import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress } from '@mui/material';
import { Container, Row } from 'react-bootstrap';
import Navigation from '../components/Navigation';
import EventSelectionLanding from '../components/EventSelectionLanding';
import { startCompanion, exitCompanion } from '../redux/companionSlice';
import { addEvenementSelect, addPlan, resetEvenements } from '../redux/evenementsSlice';
import { addHistorique } from '../redux/profilSlice';
import { evenementSelectRedux } from '../redux/evenementsServices';
import { resolveCompanion } from '../services/companionService';
import { capture } from '../services/analytics';
import { URL } from '../redux/api';

export default function CompanionLanding() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const access = useSelector(state => state.user.access);
  const session = useSelector(state => state.companion);
  const contenusBO = useSelector(state => state.profil.contenusTexte);
  const [searchParams] = useSearchParams();
  const qrSource = searchParams.get('qr_source') || 'autre';
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [events, setEvents] = useState([]);
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    if (session.token || session.expired) return undefined;
    const controller = new AbortController();
    const nextSessionId = `companion-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setSessionId(nextSessionId);
    setError('');
    capture('companion_access_started', { entry_method: 'direct' });
    const resolve = async () => {
      try {
        const { data } = await resolveCompanion(nextSessionId, controller.signal, qrSource);
        if (controller.signal.aborted) return;
        if (access) {
          const response = await evenementSelectRedux(access, data.event.id);
          if (controller.signal.aborted) return;
          dispatch(resetEvenements());
          dispatch(addEvenementSelect(response.data));
          dispatch(addPlan(data.event.plan ? `${URL}/plan-view/${data.event.id}/preview` : ''));
          dispatch(addHistorique('/'));
          capture('companion_link_authenticated_redirected', { event_id: data.event.id });
          navigate(`/dashboard/${data.event.id}`, { replace: true });
        } else {
          const availableEvents = data.events?.length ? data.events : [data.event];
          setEvents(availableEvents);
          if (availableEvents.length === 1) {
            const selected = availableEvents[0];
            dispatch(
              startCompanion({
                event: selected,
                token: selected.guest_token || data.guest_token,
                sessionId: nextSessionId,
              })
            );
            navigate(`/dashboard/${selected.id}`, { replace: true });
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(
            err.response?.data?.detail ||
              'Impossible d’accéder au mode invité. Vérifiez votre connexion et réessayez.'
          );
        }
      }
    };
    resolve();
    return () => controller.abort();
  }, [access, attempt, dispatch, navigate, qrSource, session.token, session.expired]);

  if (session.token) return <Navigate to={`/dashboard/${session.event.id}`} replace />;
  if (events.length > 1) {
    return (
      <EventSelectionLanding
        events={events}
        onSelect={event => {
          dispatch(startCompanion({ event, token: event.guest_token, sessionId }));
          navigate(`/dashboard/${event.id}`, { replace: true });
        }}
        navigationProps={{
          button: false,
          companion: true,
          retour: '',
          titre: contenusBO?.message_introduction_event ? (
            <div
              dangerouslySetInnerHTML={{
                __html: contenusBO.message_introduction_event,
              }}
            />
          ) : (
            'Choisissez votre événement'
          ),
          menus: true,
        }}
      />
    );
  }
  return (
    <Container fluid>
      <Row>
        <Navigation companion={true} menus={true} titre="Mode invité" />
      </Row>
      <section className="d-flex flex-column align-items-center gap-3 my-5 text-center">
        <h1>{session.expired ? 'La session en mode invité a expiré' : 'Mode invité'}</h1>
        {session.expired ? (
          <>
            <p>L’événement est terminé ou cet accès n’est plus disponible.</p>
            <Button variant="contained" onClick={() => dispatch(exitCompanion())}>
              Rechercher un événement en cours
            </Button>
          </>
        ) : error ? (
          <>
            <Alert severity="info">{error}</Alert>
            <Button variant="contained" onClick={() => setAttempt(value => value + 1)}>
              Réessayer
            </Button>
          </>
        ) : (
          <>
            <CircularProgress aria-label="Chargement de l’événement" />
            <p>Connexion à l’événement en cours…</p>
          </>
        )}
      </section>
    </Container>
  );
}
